import { LucideIcon, AlertCircle, Ban, CheckCircle2, Clock, XCircle } from 'lucide-react'
import { cn } from '@/common/utils/cn'
import { formatDateTime } from '@/common/utils/formatters'
import { ManualEntryProposalShowData } from '../../types/maintenanceManualEntryProposal.type'

type Props = {
  proposal: ManualEntryProposalShowData
}

type Banner = {
  icon: LucideIcon
  className: string
  title: string
  description?: string
  moment?: string | null
  reason?: string | null
}

const dateTime = (value?: string | null) => (value ? formatDateTime(new Date(value)) : null)

const resolve = (proposal: ManualEntryProposalShowData): Banner | null => {
  if (proposal.establishment_rejected_at) {
    return {
      icon: XCircle,
      className: 'bg-danger-soft/15 text-danger-soft',
      title: 'Você recusou esta proposta',
      moment: proposal.establishment_rejected_at,
      reason: proposal.establishment_rejected_reason,
    }
  }

  if (proposal.client_rejected_at) {
    return {
      icon: XCircle,
      className: 'bg-danger-soft/15 text-danger-soft',
      title: 'O cliente recusou esta proposta',
      moment: proposal.client_rejected_at,
    }
  }

  if (proposal.canceled_at) {
    return { icon: Ban, className: 'bg-white-200 text-white-400', title: 'Proposta cancelada', moment: proposal.canceled_at }
  }

  if (proposal.status === 'completed') {
    return {
      icon: CheckCircle2,
      className: 'bg-success-op/40 text-valid',
      title: 'Proposta aceita',
      moment: proposal.client_accepted_at || proposal.establishment_accepted_at,
    }
  }

  if (proposal.status === 'awaiting_establishment_response') {
    return {
      icon: AlertCircle,
      className: 'bg-yellow-400/15 text-yellow-400',
      title: 'Aguardando a sua resposta',
      description: 'Você ainda não enviou uma proposta para este processo. Adicione uma proposta para enviar ao cliente.',
    }
  }

  if (proposal.status === 'awaiting_client_response') {
    return {
      icon: Clock,
      className: 'bg-primary-blue/15 text-primary-blue',
      title: 'Aguardando resposta do cliente',
      description: 'Sua proposta foi enviada. Nenhuma ação é necessária no momento.',
    }
  }

  return null
}

const ProposalStatusBanner = ({ proposal }: Props) => {
  const banner = resolve(proposal)

  if (!banner) {
    return null
  }

  const { icon: Icon, className, title, description, moment, reason } = banner
  const momentLabel = dateTime(moment)

  return (
    <div className={cn('flex items-start gap-3 rounded-lg border border-border-default p-4', className)}>
      <Icon className="size-6 shrink-0" strokeWidth={2} />

      <div className="flex flex-col gap-0.5 min-w-0">
        <p className="text-sm font-bold">
          {title}
          {momentLabel && <span className="font-normal opacity-80"> · {momentLabel}</span>}
        </p>

        {description && <p className="text-xs text-white-400">{description}</p>}
        {reason && <p className="text-xs opacity-80">Motivo: {reason}</p>}
      </div>
    </div>
  )
}

export default ProposalStatusBanner
